import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EyeIcon, EyeOff } from 'lucide-react';
import { Button, Form, Input, Label } from '@shared/components';
import {
    dealerSecretSettingSchema,
    type DealerSecretSettingFormInput,
    type DealerSecretSettingFormOutput,
} from '../../schemas/dealerSecretSetting.schema';

interface Props {
    inputId: string;
    label: string;
    submitLabel: string;
    pending: boolean;
    disabled: boolean;
    fieldError: string | null;
    focusOnMount: boolean;
    onSubmit: (secret: string) => void;
    onCancel: () => void;
}

export function SecretSettingForm({ inputId, label, submitLabel, pending, disabled, fieldError, focusOnMount, onSubmit, onCancel }: Props) {
    const [visible, setVisible] = useState(false);
    const { control, handleSubmit, formState: { isValid } } = useForm<
        DealerSecretSettingFormInput,
        unknown,
        DealerSecretSettingFormOutput
    >({
        mode: 'onChange',
        resolver: zodResolver(dealerSecretSettingSchema),
        defaultValues: { secret: '' },
    });

    return (
        <Form onSubmit={handleSubmit(({ secret }) => onSubmit(secret))} className="space-y-4">
            <div>
                <Label htmlFor={inputId} required>{label}</Label>
                <Controller
                    name="secret"
                    control={control}
                    render={({ field, fieldState: { error, isDirty } }) => {
                        const message = error?.message ?? (isDirty ? undefined : fieldError) ?? undefined;
                        return (
                            <Input
                                id={inputId}
                                type={visible ? 'text' : 'password'}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={Boolean(message)}
                                hint={message}
                                hintId={`${inputId}-hint`}
                                aria-invalid={Boolean(message)}
                                aria-describedby={message ? `${inputId}-hint` : undefined}
                                autoComplete="new-password"
                                autoFocus={focusOnMount}
                            >
                                <button
                                    type="button"
                                    onClick={() => setVisible((v) => !v)}
                                    className="absolute z-30 -translate-y-1/2 right-3 top-1/2"
                                    aria-label={visible ? 'Hide key' : 'Show key'}
                                >
                                    {visible ? <EyeIcon size={18} /> : <EyeOff size={18} />}
                                </button>
                            </Input>
                        );
                    }}
                />
            </div>
            <div className="flex items-center gap-2">
                <Button size="sm" disabled={!isValid || pending || disabled}>
                    {pending ? 'Saving...' : submitLabel}
                </Button>
                <Button size="sm" variant="outline" type="button" onClick={onCancel} disabled={pending}>
                    Cancel
                </Button>
            </div>
        </Form>
    );
}
